import React, { useState, useEffect, useCallback } from 'react';
import { AuthService } from '../services/authService';
import { LeaderboardEntry, ApiService, ELO_VISIBLE } from '../services/apiService';
import { eloNumberTitle } from '../utils/eloUtils';
import PageCloseButton from './PageCloseButton';
import './LeaderboardPage.css';

interface LeaderboardPageProps {
  onBack: () => void;
  authState?: {
    isAuthenticated: boolean;
    user: {
      id: string;
      username: string;
      email: string;
    } | null;
  };
  onViewPlayerStats?: (userId: number, username: string) => void;
  onWatchGame?: (gameId: string) => void;
  socket?: any;
}

type Period = 'alltime' | 'monthly' | 'weekly' | 'daily';

const LeaderboardPage: React.FC<LeaderboardPageProps> = ({
  onBack,
  authState,
  onViewPlayerStats,
  onWatchGame,
}) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'ratingStandard' | 'ratingFast' | 'wins' | 'games' | 'winRate'>('wins');
  const [period, setPeriod] = useState<Period>('alltime');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<LeaderboardEntry | null>(null);
  const [challenging, setChallenging] = useState(false);
  const [challengeMessage, setChallengeMessage] = useState<{
    type: 'success' | 'error';
    text: string;
    gameId?: string | null;
  } | null>(null);

  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await AuthService.getLeaderboard(period);
      if (result.error) {
        setError(result.error);
      } else {
        setLeaderboard(result.leaderboard);
      }
    } catch (err) {
      setError('Failed to load leaderboard');
      console.error('Error loading leaderboard:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  const loadCurrentUser = useCallback(async () => {
    try {
      const user = AuthService.getCurrentUser();
      if (user) {
        setCurrentUserId(user.uid);
      }
    } catch (err) {
      console.error('Error loading current user:', err);
    }
  }, []);

  useEffect(() => {
    loadLeaderboard();
    loadCurrentUser();

    document.body.classList.add('leaderboard-page-open');
    document.documentElement.classList.add('leaderboard-page-open');

    return () => {
      document.body.classList.remove('leaderboard-page-open');
      document.documentElement.classList.remove('leaderboard-page-open');
    };
  }, [loadLeaderboard, loadCurrentUser]);

  const getRankIcon = (index: number) => {
    switch (index) {
      case 0: return '\u{1F947}';
      case 1: return '\u{1F948}';
      case 2: return '\u{1F949}';
      default: return `#${index + 1}`;
    }
  };

  const getWinRate = (user: LeaderboardEntry) => {
    if (user.winRate !== undefined && user.winRate !== null) return Math.round(user.winRate);
    const totalGames = user.total_games || 0;
    const wins = user.wins || 0;
    return totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0;
  };

  const isProvisionalRating = (user: LeaderboardEntry, pool: 'fast' | 'standard' = 'standard') =>
    (pool === 'fast' ? (user.rated_games_fast ?? 0) : (user.rated_games_standard ?? user.rated_games_count ?? 0)) < 10;

  const getEloRounded = (user: LeaderboardEntry, pool: 'fast' | 'standard' = 'standard') =>
    Math.round(pool === 'fast' ? (user.elo_fast ?? 1200) : (user.elo_standard ?? user.elo_rating ?? 0));

  const getRankColor = (index: number) => {
    switch (index) {
      case 0: return '#FFD700';
      case 1: return '#C0C0C0';
      case 2: return '#CD7F32';
      default: return '#4A90E2';
    }
  };

  const getRankingText = () => {
    switch (activeTab) {
      case 'ratingStandard':
        return {
          main: 'Ranked by Standard Elo (over 3 minutes)',
          sub: 'Hidden until 10 rated Standard games. ? means provisional.'
        };
      case 'ratingFast':
        return {
          main: 'Ranked by Fast Elo (3 minutes and below)',
          sub: 'Hidden until 10 rated Fast games. ? means provisional.'
        };
      case 'wins':
        return { main: 'Ranked by total victories Human v. Human', sub: null };
      case 'games':
        return { main: 'Ranked by Most Games Human v. Human', sub: null };
      case 'winRate':
        return {
          main: 'Ranked by Win Rate Human v. Human',
          sub: '(must have minimum 10 games played)'
        };
      default:
        return { main: 'Ranked by total victories Human v. Human', sub: null };
    }
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'daily': return 'Today';
      case 'weekly': return 'This Week';
      case 'monthly': return 'This Month';
      default: return 'All Time';
    }
  };

  const getSortedLeaderboard = () => {
    let sorted = [...leaderboard];

    switch (activeTab) {
      case 'ratingStandard':
        sorted = sorted.filter(user => (user.rated_games_standard ?? user.rated_games_count ?? 0) >= 10);
        return sorted.sort((a, b) => (b.elo_standard ?? b.elo_rating ?? 0) - (a.elo_standard ?? a.elo_rating ?? 0));
      case 'ratingFast':
        sorted = sorted.filter(user => (user.rated_games_fast ?? 0) >= 10);
        return sorted.sort((a, b) => (b.elo_fast ?? 0) - (a.elo_fast ?? 0));
      case 'wins':
        return sorted.sort((a, b) => (b.wins || 0) - (a.wins || 0));
      case 'games':
        return sorted.sort((a, b) => (b.total_games || 0) - (a.total_games || 0));
      case 'winRate':
        sorted = sorted.filter(user => (user.total_games || 0) > 10);
        return sorted.sort((a, b) => {
          const aRate = getWinRate(a);
          const bRate = getWinRate(b);
          if (bRate === aRate) {
            return (b.total_games || 0) - (a.total_games || 0);
          }
          return bRate - aRate;
        });
      default:
        return sorted;
    }
  };

  const handlePlayerClick = (player: LeaderboardEntry) => {
    setSelectedPlayer(player);
    setChallengeMessage(null);
  };

  const handleCloseProfile = () => {
    setSelectedPlayer(null);
    setChallengeMessage(null);
  };

  const handleChallenge = async () => {
    if (!selectedPlayer) return;
    setChallenging(true);
    setChallengeMessage(null);
    try {
      const result = await ApiService.sendChallenge(selectedPlayer.id);
      if (result.success) {
        setChallengeMessage({ type: 'success', text: `Challenge sent to ${selectedPlayer.username}!` });
      } else {
        let errorMessage = result.error || 'Failed to send challenge';
        let activeGameId: string | null = null;
        if (result.error?.includes('already in a game')) {
          errorMessage = `${selectedPlayer.username} is currently in a game.`;
          activeGameId = result.gameId || null;
        }
        setChallengeMessage({ type: 'error', text: errorMessage, gameId: activeGameId });
      }
    } catch (err) {
      setChallengeMessage({ type: 'error', text: 'Failed to send challenge' });
    } finally {
      setChallenging(false);
    }
  };

  const sortedList = getSortedLeaderboard();

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-container">
        {/* Header */}
        <div className="leaderboard-header">
          <PageCloseButton onClick={onBack} />
          <h1>LEADERBOARD</h1>
        </div>

        {/* Content: toolbar (fixed) + list scrolls independently */}
        <div className="leaderboard-content">
          <div className="leaderboard-main">
            {loading ? (
              <div className="leaderboard-list-scroll leaderboard-list-scroll--centered">
                <div className="loading-section">
                  <div className="loading-spinner"></div>
                  <p>Loading top players...</p>
                </div>
              </div>
            ) : error ? (
              <div className="leaderboard-list-scroll leaderboard-list-scroll--centered">
                <div className="error-section">
                  <p>{error}</p>
                  <button onClick={loadLeaderboard} className="retry-btn">
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="leaderboard-toolbar">
                  {/* Leaderboard Stats */}
                  <div className="leaderboard-stats">
                    <h2>Top {Math.min(sortedList.length, 20)} Players &mdash; {getPeriodLabel()}</h2>
                    <p>
                      {getRankingText().main}
                      {getRankingText().sub && (
                        <>
                          <br />
                          {getRankingText().sub}
                        </>
                      )}
                    </p>
                  </div>

                  {/* Time Period Tabs */}
                  <div className="leaderboard-tabs">
                    {(['daily', 'weekly', 'monthly', 'alltime'] as Period[]).map((p) => (
                      <button
                        key={p}
                        className={`tab-button ${period === p ? 'active' : ''}`}
                        onClick={() => setPeriod(p)}
                      >
                        <span className="tab-label">
                          {p === 'daily' ? 'Daily' : p === 'weekly' ? 'Weekly' : p === 'monthly' ? 'Monthly' : 'All Time'}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Sort Tabs */}
                  <div className="leaderboard-tabs leaderboard-tabs--sort">
                    <button
                      className={`tab-button ${activeTab === 'wins' ? 'active' : ''}`}
                      onClick={() => setActiveTab('wins')}
                    >
                      <span className="tab-label">Most Wins</span>
                    </button>
                    <button
                      className={`tab-button ${activeTab === 'games' ? 'active' : ''}`}
                      onClick={() => setActiveTab('games')}
                    >
                      <span className="tab-label">Most Games</span>
                    </button>
                    <button
                      className={`tab-button ${activeTab === 'winRate' ? 'active' : ''}`}
                      onClick={() => setActiveTab('winRate')}
                    >
                      <span className="tab-label">Win Rate</span>
                    </button>
                    {ELO_VISIBLE && (
                      <>
                        <button
                          className={`tab-button ${activeTab === 'ratingStandard' ? 'active' : ''}`}
                          onClick={() => setActiveTab('ratingStandard')}
                        >
                          <span className="tab-label">Elo Std</span>
                        </button>
                        <button
                          className={`tab-button ${activeTab === 'ratingFast' ? 'active' : ''}`}
                          onClick={() => setActiveTab('ratingFast')}
                        >
                          <span className="tab-label">Elo Fast</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="leaderboard-list-scroll">
                  <div className="leaderboard-list">
                    {sortedList.length === 0 ? (
                      <div className="no-data">
                        <div className="no-data-icon">&#127918;</div>
                        <h3>No Players Yet!</h3>
                        <p>
                          {activeTab === 'ratingStandard'
                            ? 'Hidden until a player has 10 rated Standard games.'
                            : activeTab === 'ratingFast'
                              ? 'Hidden until a player has 10 rated Fast games.'
                              : 'Be the first to play and appear on the leaderboard!'}
                        </p>
                      </div>
                    ) : (
                      sortedList.map((user, index) => {
                        const isCurrentUser = currentUserId && user.id === parseInt(currentUserId);
                        const isEloTab = activeTab === 'ratingStandard' || activeTab === 'ratingFast';
                        const listPool = activeTab === 'ratingFast' ? 'fast' : 'standard';
                        return (
                          <div
                            key={user.id}
                            className={`leaderboard-item ${index < 3 ? 'top-three' : ''} ${isCurrentUser ? 'current-user' : ''}`}
                            onClick={() => handlePlayerClick(user)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="rank-section">
                              <div
                                className="rank-icon"
                                style={{ color: getRankColor(index) }}
                              >
                                {getRankIcon(index)}
                              </div>
                              <div className="rank-number">#{index + 1}</div>
                            </div>

                            <div className="leaderboard-player-info">
                              <div className="leaderboard-name-row">
                                <div className="player-name">{user.username}</div>
                                {(user.is_online === true || user.is_online === 1) && (
                                  <span className="leaderboard-online-badge" title="Online">
                                    Online
                                  </span>
                                )}
                              </div>
                              {ELO_VISIBLE && isEloTab && (
                                <div
                                  className="leaderboard-elo-row"
                                  aria-label="Elo rating"
                                  title={eloNumberTitle(
                                    listPool,
                                    listPool === 'fast'
                                      ? user.rated_games_fast
                                      : (user.rated_games_standard ?? user.rated_games_count)
                                  )}
                                >
                                  <span className="leaderboard-elo-number">{getEloRounded(user, listPool)}</span>
                                  {isProvisionalRating(user, listPool) && (
                                    <span className="leaderboard-elo-provisional">?</span>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="player-stats">
                              {ELO_VISIBLE && isEloTab && (
                                <div className="stat-item">
                                  <div className="stat-content">
                                    <div
                                      className="stat-value"
                                      title={eloNumberTitle(
                                        listPool,
                                        listPool === 'fast'
                                          ? user.rated_games_fast
                                          : (user.rated_games_standard ?? user.rated_games_count)
                                      )}
                                    >
                                      {getEloRounded(user, listPool)}
                                      {isProvisionalRating(user, listPool) && (
                                        <span className="leaderboard-elo-provisional stat-value-provisional">?</span>
                                      )}
                                    </div>
                                    <div className="stat-label">{listPool === 'fast' ? 'Fast' : 'Standard'}</div>
                                  </div>
                                </div>
                              )}
                              <div className="stat-item">
                                <div className="stat-content">
                                  <div className="stat-value">{user.wins || 0}</div>
                                  <div className="stat-label">Wins</div>
                                </div>
                              </div>

                              <div className="stat-item">
                                <div className="stat-content">
                                  <div className="stat-value">{user.total_games || 0}</div>
                                  <div className="stat-label">Games</div>
                                </div>
                              </div>

                              <div className="stat-item">
                                <div className="stat-content">
                                  <div className="stat-value">{getWinRate(user)}%</div>
                                  <div className="stat-label">Win Rate</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

      </div>

      {/* Player Profile Modal */}
      {selectedPlayer && (
        <div className="lb-modal-overlay" onClick={handleCloseProfile}>
          <div className="lb-modal" onClick={(e) => e.stopPropagation()}>
            <button className="lb-modal-close" onClick={handleCloseProfile}>&times;</button>
            <div className="lb-modal-header">
              <h2>{selectedPlayer.username}</h2>
            </div>
            <div className="lb-modal-stats">
              <div className="lb-modal-stat">
                <span className="lb-modal-stat-label">Wins:</span>
                <span className="lb-modal-stat-value">{selectedPlayer.wins}</span>
              </div>
              <div className="lb-modal-stat">
                <span className="lb-modal-stat-label">Losses:</span>
                <span className="lb-modal-stat-value">{selectedPlayer.losses}</span>
              </div>
              <div className="lb-modal-stat">
                <span className="lb-modal-stat-label">Draws:</span>
                <span className="lb-modal-stat-value">{selectedPlayer.draws}</span>
              </div>
              <div className="lb-modal-stat">
                <span className="lb-modal-stat-label">Total Games:</span>
                <span className="lb-modal-stat-value">{selectedPlayer.total_games}</span>
              </div>
              <div className="lb-modal-stat">
                <span className="lb-modal-stat-label">Win Rate:</span>
                <span className="lb-modal-stat-value">{getWinRate(selectedPlayer)}%</span>
              </div>
            </div>

            <div className="lb-modal-actions">
              {authState?.isAuthenticated && onViewPlayerStats && (
                <button
                  className="lb-modal-btn lb-modal-btn-stats"
                  onClick={() => {
                    onViewPlayerStats(selectedPlayer.id, selectedPlayer.username);
                    handleCloseProfile();
                  }}
                >
                  View Full Stats
                </button>
              )}
              {authState?.isAuthenticated && currentUserId && selectedPlayer.id !== parseInt(currentUserId) && (
                (selectedPlayer.is_online === true || selectedPlayer.is_online === 1) ? (
                  <button
                    className="lb-modal-btn lb-modal-btn-challenge"
                    onClick={handleChallenge}
                    disabled={challenging}
                  >
                    {challenging ? 'Sending...' : `Challenge ${selectedPlayer.username}`}
                  </button>
                ) : (
                  <div className="lb-modal-offline">Player is not online</div>
                )
              )}

              {challengeMessage && (
                <div className={`lb-modal-message lb-modal-message-${challengeMessage.type}`}>
                  {challengeMessage.text}
                  {challengeMessage.gameId && onWatchGame && (
                    <button
                      className="lb-modal-watch-btn"
                      onClick={() => onWatchGame(challengeMessage.gameId!)}
                    >
                      Watch in YugoVision
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderboardPage;
